import { useState } from 'react';
import { LinkBase, useCanAccess, useCreatePath, useNotify } from 'ra-core';
import {
  provisionnerUnCompte,
  rattacherUnePersonne,
} from '@groupe-trousseau/shared/api-client';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { deballer, lireLErreur } from '../../fournisseurs/client';
import { oublier } from '../../fournisseurs/curseurs';
import { useStructureCourante } from '../../fournisseurs/structure-courante';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **CRÉER UN COMPTE ET LE RATTACHER — DEUX ROUTES, UN SEUL ÉCRAN, ET C'EST LA
 * CONDITION QUE D-108 POSE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `POST /api/v1/provisionnement/comptes` (F-03) et
 * `POST /api/v1/admin/structures/:id/rattachements` (F-60) sont livrées depuis le **LOT-1**,
 * et **aucune surface ne les appelait**. Elles sont deux des trois routes que D-108 a
 * comptées : « **`provisionnerUnCompte` et `rattacherUnePersonne` forment ensemble le seul
 * chemin de naissance d'un compte dans une structure**. Sans elles, aucune structure cliente
 * ne se met en service autrement que par le semis — et **la recette ne s'en aperçoit pas,
 * parce que ses sept comptes sont semés**. »
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **POURQUOI DEUX APPELS ET NON UN, ET CE N'EST PAS UN CHOIX D'ÉCRAN.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * La route de provisionnement **rattache elle-même**, mais seulement à la structure de son
 * appelant : `provisioning.service.ts` lit `organisationAppelante(caller)`, qui rend
 * `caller.organizationId` pour un secret de structure et le rattachement d'un `orgadmin`.
 * **Pour le compte qui voit tout, elle rend `null`** — Q535, « il n'a jamais de
 * rattachement » —, donc le bloc `if (organizationId !== null)` ne s'exécute pas et la
 * réponse revient avec **`rattachementCree: false`**.
 *
 * **Le compte est alors créé HORS de toute structure**, ce qui est exactement le défaut que
 * D-108 nomme : « un écran qui ne ferait que le premier geste **reproduirait le défaut qu'on
 * vient de nommer, un pas plus loin** ». Le second appel n'est donc pas un confort de
 * parcours : **il est ce qui fait entrer la personne dans la structure courante.**
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CET ÉCRAN EST CELUI DU COMPTE QUI VOIT TOUT, ET C'EST L'INTERSECTION DES DEUX
 * ROUTES QUI LE DÉCIDE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * §6.14 accorde `compte | Créer` au **`superadmin` seul** — l'`orgadmin` y porte « **NON** »,
 * motif F-60 : « **une structure rattache et détache, elle ne crée pas** ». Le `@Allow()` de
 * la route recopie cette colonne, `SECRET_IDENTITY` et `SUPERADMIN`, et sa description le dit
 * en propre : « elle **n'est pas ouverte à un `orgadmin`** ». Le rattachement, lui, est aux
 * deux rôles.
 *
 * **L'intersection des deux gestes est donc le compte qui voit tout, et l'écran ne paraît
 * qu'à lui** — la matrice répond `false` à l'autre, et `useCanAccess` le lit. **Les deux
 * cellules ont été relues au cahier des charges avant d'écrire cette ligne**, comme la reprise
 * l'exigeait : elles s'accordent avec les `@Allow()` livrés, donc ce n'est pas un D-099 de
 * plus.
 *
 * **ET LA SYMÉTRIE AVEC D-082 EST EXACTE, CE QUI ACHÈVE DE MONTRER QUE LE PARTAGE EST VOULU.**
 * Chaque rôle qui administre a **un** chemin pour faire entrer des personnes dans une
 * structure, et il n'en a qu'un : l'`orgadmin` a **l'import par fichier**, retiré au compte
 * qui voit tout parce qu'« un import s'applique à une structure et ce compte n'en a aucune » ;
 * le compte qui voit tout a **cet écran**, fermé à l'`orgadmin` parce qu'une structure ne crée
 * pas de compte. D-082 l'écrivait déjà comme l'issue qu'elle laissait ouverte : « **il crée la
 * personne par `POST /provisionnement/comptes`, puis la rattache par
 * `POST /api/v1/admin/structures/:id/rattachements` — un geste à la fois.** »
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CINQ CHAMPS, LISTE FERMÉE — ET LES DEUX CHAMPS FACULTATIFS DU CONTRAT SONT ABSENTS
 * PARCE QU'ILS N'AGISSENT PAS ICI.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Q5 et F-03 : « cinq champs, liste fermée : nom, prénom, adresse email, rôle, identifiant de
 * la personne chez la structure. **Rien d'autre.** » **Aucune date de naissance** : « le
 * produit ne sait pas qui est mineur ». Le serveur le tient par `strictObject` et le code
 * `CHAMP_NON_ACCEPTE` ; ce formulaire n'en porte pas un sixième.
 *
 * Le contrat porte deux champs facultatifs de plus, `relationAvec` et `cours`, et **ce
 * formulaire ne les offre pas — parce qu'aucun des deux n'aboutit sur ce chemin. Mesuré, pas
 * déduit :**
 *
 * - **`relationAvec` est accepté et IGNORÉ EN SILENCE.** Le service pose la relation sous
 *   `body.relationAvec !== null && organizationId !== null`, et l'organisation d'un
 *   `superadmin` est `null` — F-08 exigeant « toujours un créateur : l'appel d'une
 *   structure ». Éprouvé le 20260827 : la réponse rend `relationCreee: false` et le compte de
 *   `Relationship` **ne bouge pas**. Un champ que le seul rôle humain de cette route ne peut
 *   pas faire agir.
 * - **`cours` est REFUSÉ, et il porte son nom** : `RELATION_REQUISE`, « la relation est le
 *   préalable, **y compris pour le compte qui voit tout** ». Celui-là ne se tait pas.
 *
 * Les deux ne servent donc qu'à l'identité machine d'une structure — le CRM, D-008. **Le geste
 * de mise en relation vit sur la fiche du compte**, `CreationDeRelation`, et il est celui de
 * l'`orgadmin` depuis D-101 : c'est là que cet écran renvoie.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **UN SEUL CHAMP D'IDENTIFIANT EXTERNE POUR DEUX APPELS, ET C'EST LE SECOND QUI
 * L'ENREGISTRE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `identifiantExterne` est **obligatoire** au contrat de provisionnement — « identifiant de la
 * personne chez la structure appelante » — et **facultatif** sur le rattachement (Q533). Or le
 * premier appel ne l'écrit que dans le rattachement qu'il crée, donc **ici il est reçu et
 * jeté** : c'est `rattacherUnePersonne` qui le porte jusqu'à la ligne. Le formulaire le demande
 * donc **une fois** et le passe aux deux, plutôt que d'afficher deux champs pour une valeur.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **L'ÉCRAN N'AFFIRME PAS L'ENVOI DE L'EMAIL, PARCE QUE LA RÉPONSE NE LE PORTE PAS.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Q168 : « un email d'activation part à chaque création unitaire ». C'est une règle du
 * produit, et le bandeau la dit **comme une règle** — pas comme un fait constaté. Le corps
 * rendu par la route ne porte **rien** sur l'envoi, et `EmailService.send` **absorbe** son
 * échec en le journalisant : « il n'y a pas de table de sortie : ce message est perdu ».
 * Écrire « un email vient de partir » serait donc **une garantie affirmée dont l'exécution
 * repose sur une valeur que rien ne contraint** — la forme exacte de D-096 et de D-088. En
 * local, où `EMAIL_*` n'est pas configurée, la phrase serait même fausse à tous les coups.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LA COUTURE ENTRE LES DEUX APPELS N'EST PAS UNE TRANSACTION, ET L'ÉCRAN LE DIT.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Deux routes, deux transactions : si la seconde échoue, **le compte existe et n'est rattaché
 * à rien**. C'est le défaut de D-108 reproduit par accident, et **le taire serait pire que de
 * l'avoir** : l'écran garde l'identifiant rendu par le premier appel, nomme l'état atteint, et
 * offre de **finir le rattachement seul**. Rien n'est inventé — la valeur affichée est celle
 * que le serveur a rendue.
 *
 * **Reprendre le formulaire entier est sûr aussi**, et c'est ce qui rend cette couture
 * tenable : l'adresse est la clé de reconnaissance, donc un second provisionnement de la même
 * adresse **retrouve le compte au lieu d'en créer un second** (Q3, Q51) et rend
 * `compteDejaExistant: true`.
 *
 * **Le refus le plus fréquent du second appel n'est d'ailleurs pas une panne** : une personne
 * déjà rattachée à cette structure est refusée, « un second rattachement actif la ferait
 * compter deux fois dans ses listes ». Le message du serveur distingue ce cas ; **le code, non**
 * — `ROLE_INCOMPATIBLE` couvre les trois refus de cette route —, donc cet écran affiche le
 * message plutôt que de brancher sur le code.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **POURQUOI CE N'EST PAS UN `<Create>` DE LA PEAU.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Deux raisons, et la première est mécanique : **le fournisseur de données refuse `create` sur
 * `comptes`**, en levant — « rien ne se crée sous "comptes" par ce chemin ». La seconde est la
 * règle : `DOCTRINE.md` §7, « une action métier custom est **du React ordinaire** », et « on ne
 * cherche jamais à faire entrer une action complexe dans un formulaire de ressource ». Deux
 * appels enchaînés, dont le second dépend de la réponse du premier, en est une.
 *
 * L'écran est **monté comme le `create` de la ressource** malgré cela, comme
 * `CreationDeStructure` : c'est l'adresse que la peau donne au geste, et la garder évite un
 * second chemin vers lui. **Mais `hasCreate` de `ra-core` ne consulte aucun droit** — il ne
 * regarde que la présence de la propriété —, donc le bouton de la liste est gardé chez elle.
 */

/** Les deux rôles que le contrat de provisionnement accepte, et il n'en accepte pas quatre. */
const ROLES: readonly { readonly valeur: 'tutor' | 'student'; readonly nom: string }[] = [
  { valeur: 'tutor', nom: 'Tuteur' },
  { valeur: 'student', nom: 'Élève' },
];

type Saisie = {
  nom: string;
  prenom: string;
  email: string;
  role: 'tutor' | 'student';
  identifiantExterne: string;
};

const SAISIE_VIDE: Saisie = {
  nom: '',
  prenom: '',
  email: '',
  role: 'student',
  identifiantExterne: '',
};

/** Ce que l'écran sait après un geste, et rien de plus que ce que le serveur a rendu. */
type Aboutissement = {
  readonly compteId: string;
  readonly email: string;
  readonly compteDejaExistant: boolean;
  readonly rattache: boolean;
  /** Le refus du rattachement, quand il y en a un. Le compte existe alors sans structure. */
  readonly refusDuRattachement: string | null;
};

export function CreationDeCompte() {
  const { structureId, selecteurAffiche } = useStructureCourante();
  const notifier = useNotify();
  const cheminDe = useCreatePath();
  const { canAccess: peutCreer, isPending: droitEnCours } = useCanAccess({
    resource: 'comptes',
    action: 'create',
  });

  const [saisie, setSaisie] = useState<Saisie>(SAISIE_VIDE);
  const [enCours, setEnCours] = useState(false);
  const [abouti, setAbouti] = useState<Aboutissement | null>(null);

  const poser = <C extends keyof Saisie>(champ: C, valeur: Saisie[C]) =>
    setSaisie((precedente) => ({ ...precedente, [champ]: valeur }));

  // **Le droit se lit avant l'écran, jamais pendant.** `useCanAccess` répond en deux temps ;
  // afficher le formulaire pendant l'attente le ferait clignoter chez qui n'y a pas droit.
  if (droitEnCours) return null;

  /**
   * **Un refus explicite, et non une page vide.** F-55 : « **une absence et un refus se lisent
   * pareil** », et c'est le motif pour lequel §6.14 écrit ses cellules fermées au lieu de les
   * omettre. Un `orgadmin` qui atteint cette adresse à la main lit donc pourquoi, et par où
   * passe son geste.
   */
  if (!peutCreer) {
    return (
      <main className="p-6">
        <Alert>
          <AlertTitle>Créer un compte n’est pas un geste d’une structure.</AlertTitle>
          <AlertDescription>
            « Une structure rattache et détache, elle ne crée pas » : un compte appartient à la
            personne, et son rôle vaut chez toutes ses structures. Pour faire entrer des
            personnes dans votre structure, employez l’import par fichier, sur la fiche de la
            structure.
          </AlertDescription>
        </Alert>
      </main>
    );
  }

  // **F-55 critère 2, servi comme sur la liste des comptes.** Sans structure courante, il n'y
  // a personne à rattacher : le second appel n'a pas d'adresse. On l'exige avant, plutôt que
  // de créer un compte qu'on ne saurait pas rattacher.
  if (structureId === null) {
    return (
      <main className="p-6">
        <Alert>
          <AlertTitle>Choisissez une structure.</AlertTitle>
          <AlertDescription>
            {selecteurAffiche
              ? 'Un compte créé ici est rattaché à la structure courante, du même geste. ' +
                'Prenez-en une dans le sélecteur en tête de la surface : sans elle, le compte ' +
                'naîtrait hors de toute structure.'
              : 'Aucun rattachement actif n’est associé à ce compte, donc aucune structure à ' +
                'administrer.'}
          </AlertDescription>
        </Alert>
      </main>
    );
  }

  /**
   * **Le second appel, isolé — parce qu'il se rejoue seul.**
   *
   * Il est appelé par le geste complet, et par le bouton de reprise quand le premier a réussi
   * et le second échoué. Une seule écriture des deux côtés : la reprise ne peut pas dériver du
   * geste dont elle est la moitié.
   */
  const rattacher = async (compteId: string): Promise<string | null> => {
    try {
      await deballer(
        rattacherUnePersonne({
          path: { id: structureId },
          body: {
            compteId,
            // **Vide plutôt qu'une chaîne vide** : le champ est facultatif ici (Q533), et une
            // chaîne vide est une valeur, pas une absence.
            identifiantExterne:
              saisie.identifiantExterne.trim() === ''
                ? undefined
                : saisie.identifiantExterne.trim(),
          },
        }),
      );
      // **La liste des comptes vient de changer.** Ses curseurs portent sur un ordre qui a
      // bougé — F-59 ordonne par date de rattachement décroissante, donc la ligne neuve est en
      // tête —, et les garder ferait sauter ou répéter des lignes.
      oublier('comptes');
      return null;
    } catch (erreur) {
      return lireLErreur(erreur).message;
    }
  };

  const soumettre = async (evenement: React.FormEvent<HTMLFormElement>) => {
    evenement.preventDefault();
    setEnCours(true);
    setAbouti(null);
    try {
      const provisionne = await deballer(
        provisionnerUnCompte({
          body: {
            nom: saisie.nom.trim(),
            prenom: saisie.prenom.trim(),
            email: saisie.email.trim(),
            role: saisie.role,
            identifiantExterne: saisie.identifiantExterne.trim(),
          },
        }),
      );

      const refus = await rattacher(provisionne.identifiantSwype);
      setAbouti({
        compteId: provisionne.identifiantSwype,
        email: saisie.email.trim(),
        compteDejaExistant: provisionne.compteDejaExistant,
        rattache: refus === null,
        refusDuRattachement: refus,
      });
      if (refus === null) setSaisie(SAISIE_VIDE);
    } catch (erreur) {
      const lue = lireLErreur(erreur);
      notifier(
        lue.code === 'ROLE_INCOMPATIBLE'
          ? 'Cette adresse est déjà celle d’un compte portant un autre rôle. Un compte ne ' +
              'porte qu’un seul rôle, et il ne change pas.'
          : lue.message,
        { type: 'error' },
      );
    } finally {
      setEnCours(false);
    }
  };

  const reprendreLeRattachement = async () => {
    if (abouti === null) return;
    setEnCours(true);
    const refus = await rattacher(abouti.compteId);
    setAbouti({ ...abouti, rattache: refus === null, refusDuRattachement: refus });
    if (refus === null) setSaisie(SAISIE_VIDE);
    setEnCours(false);
  };

  const complet =
    saisie.nom.trim() !== '' &&
    saisie.prenom.trim() !== '' &&
    saisie.email.trim() !== '' &&
    saisie.identifiantExterne.trim() !== '';

  return (
    <main className="flex max-w-2xl flex-col gap-6 p-6">
      <h2 className="text-lg font-medium">Créer un compte et le rattacher</h2>

      {abouti !== null && abouti.rattache && (
        <Alert>
          <AlertTitle>
            {abouti.compteDejaExistant
              ? 'Cette adresse était déjà connue : son compte a été retrouvé, et il est ' +
                'désormais rattaché à cette structure.'
              : 'Le compte est créé et rattaché à cette structure.'}
          </AlertTitle>
          <AlertDescription>
            <span className="block">
              {abouti.compteDejaExistant
                ? 'Aucun second compte n’a été créé, le premier nom enregistré est conservé, ' +
                  'et aucun email n’est parti — un rattachement ajouté à un compte existant ' +
                  'n’active rien.'
                : 'Le produit fait partir un code d’activation à ' +
                  abouti.email +
                  ' — cette réponse ne le confirme pas : elle ne porte rien sur ' +
                  'l’envoi, et un email perdu ne se voit pas d’ici. La personne peut ' +
                  'le redemander depuis la page de connexion.'}
            </span>
            <span className="mt-2 block">
              Pour que cette personne puisse recevoir ou donner un cours, il lui faut une
              relation : le geste vit sur sa fiche, et c’est la structure qui le porte.
            </span>
            <LinkBase
              className={buttonVariants({ variant: 'outline', size: 'sm' }) + ' mt-3'}
              to={cheminDe({ resource: 'comptes', type: 'show', id: abouti.compteId })}
            >
              Ouvrir sa fiche
            </LinkBase>
          </AlertDescription>
        </Alert>
      )}

      {/**
       * **L'état que D-108 nomme, atteint pour de vrai — et il ne se tait pas.**
       *
       * Le compte existe, il n'est rattaché à rien, et il n'apparaîtra donc dans la liste
       * d'aucune structure. Le seul geste qui reste est le second appel ; l'écran le garde
       * sous la main plutôt que d'obliger à recommencer.
       */}
      {abouti !== null && !abouti.rattache && (
        <Alert variant="destructive">
          <AlertTitle>
            Le compte existe, et il n’est rattaché à aucune structure.
          </AlertTitle>
          <AlertDescription>
            <span className="block">{abouti.refusDuRattachement}</span>
            <span className="mt-2 block">
              Les deux gestes sont deux appels distincts : le premier a abouti, le second non.
              Tant qu’il n’aboutit pas, cette personne n’apparaît dans la liste d’aucune
              structure. Son identifiant Swype est{' '}
              <code className="font-mono text-xs">{abouti.compteId}</code>.
            </span>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              disabled={enCours}
              onClick={() => void reprendreLeRattachement()}
            >
              Rattacher à cette structure
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <form className="flex flex-col gap-4" onSubmit={(e) => void soumettre(e)}>
        <div className="flex flex-col gap-1">
          <Label htmlFor="prenom">Prénom</Label>
          <Input
            id="prenom"
            value={saisie.prenom}
            onChange={(e) => poser('prenom', e.target.value)}
            autoComplete="off"
          />
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="nom">Nom</Label>
          <Input
            id="nom"
            value={saisie.nom}
            onChange={(e) => poser('nom', e.target.value)}
            autoComplete="off"
          />
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="email">Adresse email</Label>
          <Input
            id="email"
            type="email"
            value={saisie.email}
            onChange={(e) => poser('email', e.target.value)}
            autoComplete="off"
          />
          <span className="text-muted-foreground text-xs">
            C’est la clé de reconnaissance, et seulement à ce moment-là : une adresse déjà
            connue — présente ou passée — retrouve son compte au lieu d’en créer un second. Elle
            ne se modifie plus depuis cette surface ensuite.
          </span>
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="role">Rôle</Label>
          {/* **Un `select` natif, et non un composant de la peau** : ce formulaire est une
              action métier, pas un formulaire de ressource — même choix que
              `CreationDeRelation`, et même motif, `DOCTRINE.md` §7. */}
          <select
            id="role"
            className="border-input bg-background h-9 w-fit rounded-md border px-3 text-sm"
            value={saisie.role}
            onChange={(e) => poser('role', e.target.value as 'tutor' | 'student')}
          >
            {ROLES.map((role) => (
              <option key={role.valeur} value={role.valeur}>
                {role.nom}
              </option>
            ))}
          </select>
          <span className="text-muted-foreground text-xs">
            Deux rôles, et pas quatre. Une personne qui tient une structure se désigne par un
            autre geste, et le compte qui voit tout ne se fabrique pas depuis cette surface.
          </span>
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="identifiantExterne">Identifiant chez la structure</Label>
          <Input
            id="identifiantExterne"
            value={saisie.identifiantExterne}
            onChange={(e) => poser('identifiantExterne', e.target.value)}
            autoComplete="off"
          />
          <span className="text-muted-foreground text-xs">
            Il est porté par le rattachement, pas par le compte : la même personne peut en avoir
            un différent dans deux structures. Il paraît dans la liste des comptes et dans
            l’export.
          </span>
        </div>

        <Button type="submit" disabled={enCours || !complet} className="self-start">
          Créer et rattacher
        </Button>
        <span className="text-muted-foreground text-xs">
          Cinq champs, et pas un sixième : ni date de naissance, ni téléphone, ni note libre. Le
          produit ne sait pas qui est mineur, et un champ hors de cette liste est refusé par le
          serveur plutôt qu’ignoré.
        </span>
      </form>
    </main>
  );
}
